import { priceAt } from '../../data/seed/seedReference';
import type { Application, Holder, RegistryState } from '../../data/types';
import { money } from '../../lib/format';
import { newId } from '../../lib/random';
import type { RegistryAction } from '../actions';
import {
  addApproval,
  addDocuments,
  appendLedger,
  completeTasks,
  fundCode,
  holderIdForEntity,
  today,
  withAudit,
} from '../helpers';

type Handler<T extends RegistryAction['type']> = (
  state: RegistryState,
  action: Extract<RegistryAction, { type: T }>,
) => RegistryState;

function patchApplication(
  state: RegistryState,
  id: string,
  patch: (application: Application) => Partial<Application>,
): RegistryState {
  return {
    ...state,
    applications: state.applications.map((application) =>
      application.id === id ? { ...application, ...patch(application) } : application,
    ),
  };
}

export const setApplicationStatus: Handler<'APPLICATION_SET_STATUS'> = (state, action) => {
  const application = state.applications.find((item) => item.id === action.id);
  if (!application) return state;
  const next = patchApplication(state, action.id, (item) => ({
    status: action.status,
    notes: action.note ? [...item.notes, action.note] : item.notes,
    checklist:
      action.status === 'AWAITING_FUNDS'
        ? { ...item.checklist, identity: 'DONE', owners: 'DONE' }
        : item.checklist,
  }));
  return withAudit(
    next,
    `Application moved to ${action.status}`,
    application.reference,
    action.note ?? application.applicantName,
  );
};

export const requestIssue: Handler<'APPLICATION_REQUEST_ISSUE'> = (state, action) => {
  const application = state.applications.find((item) => item.id === action.id);
  if (!application) return state;
  let next = patchApplication(state, action.id, () => ({ status: 'PENDING_APPROVAL' }));
  next = addApproval(next, {
    kind: 'UNIT_ISSUE',
    refId: application.id,
    fundId: application.fundId,
    title: `Issue units: ${application.applicantName}, ${money(application.amount)} ${fundCode(state, application.fundId)}`,
    summary: 'All checklist items complete and application monies matched.',
    amount: application.amount,
  });
  return withAudit(
    next,
    'Unit issue submitted for approval',
    application.reference,
    money(application.amount),
  );
};

export const rejectApplication: Handler<'APPLICATION_REJECT'> = (state, action) => {
  const application = state.applications.find((item) => item.id === action.id);
  if (!application) return state;
  const next = patchApplication(state, action.id, (item) => ({
    status: 'REJECTED',
    notes: [...item.notes, `Rejected: ${action.reason}`],
  }));
  return withAudit(next, 'Application rejected', application.reference, action.reason);
};

export const sendApplicationReminder: Handler<'APPLICATION_SEND_REMINDER'> = (state, action) => {
  const application = state.applications.find((item) => item.id === action.id);
  if (!application) return state;
  return withAudit(state, 'Reminder sent', application.reference, `Email to ${application.email}`);
};

export function holderFromApplication(state: RegistryState, application: Application): Holder {
  const index = state.holders.length + 1;
  return {
    id: newId('h'),
    tenantId: state.currentTenantId,
    investmentEntityId: application.investmentEntityId,
    accountNumber: `FC${200_000 + index}`,
    name: application.applicantName,
    type: application.applicantType,
    contactName: application.contactName,
    email: application.email,
    phone: '0400 000 000',
    address: {
      line1: 'Address captured in application',
      suburb: 'Melbourne',
      state: 'VIC',
      postcode: '3000',
      country: 'AU',
    },
    tfnStatus: 'QUOTED',
    tfnLast3: '000',
    residency: 'AU',
    fatca: 'NOT_REPORTABLE',
    crs: 'NOT_REPORTABLE',
    kycStatus: 'VERIFIED',
    kycVerifiedAt: today,
    kycRefreshDue: '2029-10-16',
    riskRating: 'LOW',
    pep: false,
    wholesale: {
      basis: 'ACCOUNTANT_CERTIFICATE',
      issuedAt: today,
      expiresAt: '2028-10-16',
      issuer: 'Uploaded with application',
    },
    bank: {
      bsb: '063-000',
      accountNumber: '000000000',
      accountName: application.applicantName,
      verified: true,
    },
    beneficialOwners: [],
    drpElection: false,
    communication: 'EMAIL',
    createdAt: today,
    investorLoginId: application.investorLoginId,
  };
}

export function issueUnitsForApplication(
  state: RegistryState,
  applicationId: string,
  approver: string,
): RegistryState {
  const application = state.applications.find((item) => item.id === applicationId);
  const unitClass = state.classes.find((item) => item.id === application?.classId);
  if (!application || !unitClass) return state;
  let next = state;
  let holderId = holderIdForEntity(state, application.investmentEntityId);
  if (!holderId) {
    const holder = holderFromApplication(state, application);
    holderId = holder.id;
    next = { ...next, holders: [...next.holders, holder] };
    if (application.investorLoginId) {
      next = {
        ...next,
        investorLogins: next.investorLogins.map((login) =>
          login.id === application.investorLoginId
            ? { ...login, holderIds: [...login.holderIds, holder.id] }
            : login,
        ),
      };
    }
  }
  const price = priceAt(unitClass, today);
  next = appendLedger(next, [
    {
      fundId: application.fundId,
      classId: application.classId,
      holderId,
      type: 'ISSUE',
      effectiveDate: today,
      units: Math.round((application.amount / price) * 10_000) / 10_000,
      amount: application.amount,
      unitPrice: price,
      reference: application.reference,
      memo: 'Units issued on application',
      postedBy: application.investorLoginId ? 'Investor portal' : 'Alex Morgan',
      approvedBy: approver,
    },
  ]);
  next = patchApplication(next, applicationId, () => ({ status: 'ISSUED' }));
  next = completeTasks(next, (task) => task.refId === applicationId);
  return addDocuments(next, [
    {
      holderId,
      fundId: application.fundId,
      kind: 'TRANSACTION_CONFIRMATION',
      name: `${fundCode(state, application.fundId)} unit issue confirmation (${application.reference})`,
      date: today,
      sizeKb: 72,
      visibleToInvestor: true,
    },
  ]);
}

export const saveInvestorApplication: Handler<'INVESTOR_APPLICATION_SAVE'> = (state, action) => {
  const existing = action.application.id
    ? state.applications.find((item) => item.id === action.application.id)
    : undefined;
  const id = action.application.id ?? newId('app');
  const base: Application = existing ?? {
    id,
    investmentEntityId: `ie-${id}`,
    allocationId: '',
    reference: `APP-26-${1100 + state.applications.length}`,
    fundId: action.application.fundId ?? 'f-sdf2',
    classId: action.application.classId ?? 'c-sdf2-a',
    applicantName: '',
    applicantType: 'INDIVIDUAL',
    contactName: '',
    email: '',
    amount: 0,
    status: 'IN_PROGRESS',
    createdAt: today,
    checklist: {
      entity: 'PENDING',
      owners: 'PENDING',
      identity: 'PENDING',
      tax: 'PENDING',
      bank: 'PENDING',
      wholesale: 'PENDING',
      signed: 'PENDING',
      funds: 'PENDING',
    },
    notes: [],
  };
  const merged: Application = {
    ...base,
    ...action.application,
    id: base.id,
    reference: base.reference,
  };
  if (action.submit) {
    merged.status = 'SUBMITTED';
    merged.submittedAt = today;
    merged.checklist = {
      ...merged.checklist,
      entity: 'DONE',
      owners: 'DONE',
      identity: 'DONE',
      tax: 'DONE',
      bank: 'DONE',
      wholesale: 'DONE',
      signed: 'DONE',
    };
  } else if (merged.status === 'INVITED') {
    merged.status = 'IN_PROGRESS';
  }
  const applications = existing
    ? state.applications.map((item) => (item.id === merged.id ? merged : item))
    : [merged, ...state.applications];
  let next: RegistryState = { ...state, applications };
  if (action.submit) {
    next = completeTasks(next, (task) => task.refId === merged.id);
  }
  return withAudit(
    next,
    action.submit ? 'Application submitted' : 'Application saved',
    merged.reference,
    `${merged.applicantName} · ${money(merged.amount)}`,
    'Investor portal',
  );
};
